import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, CheckCheck, Inbox } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { useNotifications } from '../../context/NotificationContext';
import { formatDateTime } from '../../lib/api';

const typeAccent = (type) => {
  switch (type) {
    case 'DONATION_VERIFIED':
    case 'CAMPAIGN_APPROVED':
    case 'CAMPAIGN_MILESTONE_50':
    case 'CAMPAIGN_MILESTONE_100':
    case 'TAX_RECEIPT_ISSUED':
      return 'bg-emerald-500';
    case 'DONATION_REJECTED':
    case 'CAMPAIGN_REJECTED':
    case 'CAMPAIGN_DELETED_BY_ADMIN':
      return 'bg-rose-500';
    case 'ADMIN_BROADCAST':
      return 'bg-amber-500';
    default:
      return 'bg-primary';
  }
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { items, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const handleItemClick = async (n) => {
    if (!n.read) await markAsRead(n.id);
    if (n.link) navigate(n.link);
  };

  const recent = items.slice(0, 8);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative rounded-full w-10 h-10 p-0"
          data-testid="notification-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] p-0 overflow-hidden"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
          <div>
            <p className="text-sm font-semibold">Notifications</p>
            <p className="text-xs text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs gap-1"
                onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                data-testid="notification-mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Read all
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {recent.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground flex flex-col items-center gap-2">
              <Inbox className="w-8 h-8 opacity-50" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs">We'll let you know when something happens.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/40">
              {recent.map((n) => (
                <li
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className={`px-4 py-3 cursor-pointer transition-colors hover:bg-secondary/60 ${
                    !n.read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleItemClick(n)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${typeAccent(n.type)} ${n.read ? 'opacity-40' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {n.body}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 mt-1">
                        {formatDateTime(n.created_at)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-border/50 px-4 py-2 bg-secondary/30">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-xs"
            onClick={() => navigate('/notifications')}
            data-testid="notification-view-all-btn"
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
